/**
 * Phone number formatting utilities
 */

/**
 * Extracts a phone number from a Twilio client identifier or returns the input unchanged.
 *
 * Twilio client identifiers have the format: `client:+14156923468-userId-orgId`
 * This function extracts just the phone number portion.
 *
 * @param input - The string that may be a client identifier or a regular phone number
 * @returns The extracted phone number, or the original input if not a client identifier
 *
 * @example
 * extractPhoneFromClientId("client:+14156923468-63a0d5e340e38ba9e8999b55-63a0d6be40e38ba9e899a490")
 * // Returns: "+14156923468"
 *
 * @example
 * extractPhoneFromClientId("+14156923468")
 * // Returns: "+14156923468" (unchanged)
 *
 * @example
 * extractPhoneFromClientId("client:14156923468-abc123")
 * // Returns: "14156923468"
 */
export function extractPhoneFromClientId(input: string | null | undefined): string {
  if (!input) return "";

  // Check if it's a Twilio client identifier (starts with "client:")
  if (!input.startsWith("client:")) {
    return input;
  }

  // Remove the "client:" prefix
  const withoutPrefix = input.slice(7);

  // The phone number is before the first dash that follows the phone digits
  // Phone numbers start with + or digit, and contain only digits after the optional +
  const match = withoutPrefix.match(/^(\+?\d+)/);

  if (match) {
    return match[1] ?? input;
  }

  // If no valid phone pattern found, return the original input
  return input;
}

/**
 * Phone number format types
 */
export type PhoneFormat = "us-standard" | "us-dashes" | "international" | "e164";

/**
 * Options for phone number formatting
 */
export interface PhoneFormatOptions {
  /** The format type to use. Defaults to "us-standard" */
  format?: PhoneFormat;
  /** Whether to include extension in formatted output. Defaults to false */
  includeExtension?: boolean;
  /** Custom extension label. Defaults to "ext." */
  extensionLabel?: string;
  /** Whether to return original input on error. Defaults to true */
  returnOriginalOnError?: boolean;
}

/**
 * Parsed phone number structure
 * @internal
 */
interface ParsedPhone {
  countryCode?: string;
  areaCode: string;
  prefix: string;
  lineNumber: string;
  extension?: string;
  hadPlusPrefix: boolean;
  isValid: boolean;
}

/**
 * Extracts extension from phone string
 * Handles formats: "ext", "x", "ext.", "extension"
 * @internal
 */
function extractExtension(phone: string): { digits: string; extension?: string } {
  // Match common extension patterns
  const extPatterns = [/^(.+?)\s+ext\.?\s*(\d+)$/i, /^(.+?)\s+extension\s+(\d+)$/i, /^(.+?)\s+x\s*(\d+)$/i];

  for (const pattern of extPatterns) {
    const match = phone.match(pattern);
    if (match) {
      const digits = match[1];
      const extension = match[2];
      if (digits !== undefined) {
        return { digits: digits.trim(), extension };
      }
    }
  }

  return { digits: phone };
}

/**
 * Extracts only digits from a phone string
 * Removes all formatting characters except + prefix
 * @internal
 */
function normalizePhone(phone: string): string {
  // Keep the + prefix if present, remove all other non-digits
  const hasPlus = phone.startsWith("+");
  const digits = phone.replace(/\D/g, "");
  return hasPlus ? `+${digits}` : digits;
}

/**
 * Validates a normalized phone number
 * Checks for 10-15 digits with optional + prefix
 * @internal
 */
function isValidPhone(normalized: string): boolean {
  const phoneRegex = /^\+?\d{10,15}$/;
  return phoneRegex.test(normalized);
}

/**
 * Splits normalized digits into components
 * Handles 10-15 digit numbers with optional country code
 * @internal
 */
function splitDigits(
  digits: string
): { countryCode?: string; areaCode: string; prefix: string; lineNumber: string } | null {
  // Remove + prefix for processing
  const cleanDigits = digits.replace(/^\+/, "");
  const length = cleanDigits.length;

  if (length === 10) {
    // US format without country code: 1234567890
    return {
      areaCode: cleanDigits.slice(0, 3),
      prefix: cleanDigits.slice(3, 6),
      lineNumber: cleanDigits.slice(6, 10),
    };
  } else if (length === 11 && cleanDigits.startsWith("1")) {
    // US with country code: 11234567890
    return {
      countryCode: "1",
      areaCode: cleanDigits.slice(1, 4),
      prefix: cleanDigits.slice(4, 7),
      lineNumber: cleanDigits.slice(7, 11),
    };
  } else if (length >= 11 && length <= 15) {
    // International: variable length country code
    // Extract last 10 digits for number, rest is country code
    const numberPart = cleanDigits.slice(-10);
    const countryCode = cleanDigits.slice(0, -10);

    return {
      countryCode,
      areaCode: numberPart.slice(0, 3),
      prefix: numberPart.slice(3, 6),
      lineNumber: numberPart.slice(6, 10),
    };
  }

  return null; // Invalid length
}

/**
 * Parses a phone number string into its components
 * @internal
 */
function parsePhoneNumber(phone: string): ParsedPhone {
  // Extract extension first
  const { digits, extension } = extractExtension(phone);

  // Check if original input has invalid characters (letters, special chars except allowed formatting)
  const allowedChars = /^[\d\s\-().+]+$/;
  if (!allowedChars.test(digits)) {
    return {
      areaCode: "",
      prefix: "",
      lineNumber: "",
      extension,
      hadPlusPrefix: false,
      isValid: false,
    };
  }

  // Track if the input had a + prefix (for E.164 formatting)
  const hadPlusPrefix = digits.trim().startsWith("+");

  // Normalize phone (remove formatting, keep + prefix)
  const normalized = normalizePhone(digits);

  // Validate
  if (!isValidPhone(normalized)) {
    return {
      areaCode: "",
      prefix: "",
      lineNumber: "",
      extension,
      hadPlusPrefix,
      isValid: false,
    };
  }

  // Split into components
  const components = splitDigits(normalized);
  if (!components) {
    return {
      areaCode: "",
      prefix: "",
      lineNumber: "",
      extension,
      hadPlusPrefix,
      isValid: false,
    };
  }

  return {
    ...components,
    extension,
    hadPlusPrefix,
    isValid: true,
  };
}

/**
 * Formats extension with label
 * @internal
 */
function formatExtension(extension: string, label: string = "ext."): string {
  return `${label} ${extension}`;
}

/**
 * Applies format to parsed phone components
 * @internal
 */
function applyFormat(parsed: ParsedPhone, format: PhoneFormat): string {
  const { countryCode, areaCode, prefix, lineNumber } = parsed;

  switch (format) {
    case "us-standard":
      return `(${areaCode}) ${prefix}-${lineNumber}`;

    case "us-dashes":
      return `${areaCode}-${prefix}-${lineNumber}`;

    case "international":
      if (countryCode) {
        return `+${countryCode} (${areaCode}) ${prefix}-${lineNumber}`;
      }
      // No country code - fall back to us-standard
      return `(${areaCode}) ${prefix}-${lineNumber}`;

    case "e164":
      if (countryCode) {
        // E.164 standard always requires + prefix with country code
        return `+${countryCode}${areaCode}${prefix}${lineNumber}`;
      }
      // No country code - return digits only (no formatting)
      return `${areaCode}${prefix}${lineNumber}`;

    default:
      return `(${areaCode}) ${prefix}-${lineNumber}`;
  }
}

/**
 * Formats a phone number string into specified format.
 *
 * Handles various input formats and normalizes them into the requested output format.
 * Returns original input if phone number is invalid or malformed.
 *
 * @param phone - The phone number to format (accepts various formats)
 * @param options - Formatting options
 * @returns Formatted phone number string, or original input if invalid
 *
 * @example
 * formatPhone("1234567890", { format: "us-standard" })
 * // Returns: "(123) 456-7890"
 *
 * @example
 * formatPhone("+11234567890", { format: "international" })
 * // Returns: "+1 (123) 456-7890"
 *
 * @example
 * formatPhone("1234567890 ext 123", { format: "us-standard", includeExtension: true })
 * // Returns: "(123) 456-7890 ext. 123"
 *
 * @example
 * formatPhone("invalid", { format: "us-standard" })
 * // Returns: "invalid" (original input preserved)
 *
 * @example
 * formatPhone(null)
 * // Returns: ""
 */
export function formatPhone(phone: string | null | undefined, options: PhoneFormatOptions = {}): string {
  // Handle null/undefined
  if (phone == null || phone === "") {
    return "";
  }

  const {
    format = "us-standard",
    includeExtension = false,
    extensionLabel = "ext.",
    returnOriginalOnError = true,
  } = options;

  // Parse phone number
  const parsed = parsePhoneNumber(phone);

  // If invalid, return original or empty string
  if (!parsed.isValid) {
    return returnOriginalOnError ? phone : "";
  }

  // Apply format
  let formatted = applyFormat(parsed, format);

  // Append extension if requested and present (E.164 doesn't support extensions)
  if (includeExtension && parsed.extension && format !== "e164") {
    formatted += ` ${formatExtension(parsed.extension, extensionLabel)}`;
  }

  return formatted;
}

/**
 * Formats phone number as US Standard: (123) 456-7890
 *
 * @param phone - The phone number to format
 * @param includeExtension - Whether to include extension. Defaults to false
 * @returns Formatted phone number
 *
 * @example
 * formatPhoneUSStandard("1234567890")
 * // Returns: "(123) 456-7890"
 *
 * @example
 * formatPhoneUSStandard("1234567890 ext 123", true)
 * // Returns: "(123) 456-7890 ext. 123"
 */
export function formatPhoneUSStandard(phone: string, includeExtension: boolean = false): string {
  return formatPhone(phone, { format: "us-standard", includeExtension });
}

/**
 * Formats phone number as US Dashes: 123-456-7890
 *
 * @param phone - The phone number to format
 * @param includeExtension - Whether to include extension. Defaults to false
 * @returns Formatted phone number
 *
 * @example
 * formatPhoneUSDashes("1234567890")
 * // Returns: "123-456-7890"
 *
 * @example
 * formatPhoneUSDashes("+11234567890")
 * // Returns: "123-456-7890"
 */
export function formatPhoneUSDashes(phone: string, includeExtension: boolean = false): string {
  return formatPhone(phone, { format: "us-dashes", includeExtension });
}

/**
 * Formats phone number as International: +1 (123) 456-7890
 *
 * Note: Does not add country code if not present in input
 *
 * @param phone - The phone number to format
 * @param includeExtension - Whether to include extension. Defaults to false
 * @returns Formatted phone number
 *
 * @example
 * formatPhoneInternational("+11234567890")
 * // Returns: "+1 (123) 456-7890"
 *
 * @example
 * formatPhoneInternational("1234567890")
 * // Returns: "(123) 456-7890" (no +1 added, falls back to US standard)
 *
 * @example
 * formatPhoneInternational("+441234567890")
 * // Returns: "+44 (123) 456-7890"
 */
export function formatPhoneInternational(phone: string, includeExtension: boolean = false): string {
  return formatPhone(phone, { format: "international", includeExtension });
}

/**
 * Formats phone number as E.164: +11234567890
 *
 * Standard international format for phone numbers.
 * Note: Does not add country code if not present in input
 *
 * @param phone - The phone number to format
 * @returns Formatted phone number (E.164 does not support extensions)
 *
 * @example
 * formatPhoneE164("+1 (123) 456-7890")
 * // Returns: "+11234567890"
 *
 * @example
 * formatPhoneE164("1234567890")
 * // Returns: "1234567890" (no +1 added)
 *
 * @example
 * formatPhoneE164("(123) 456-7890")
 * // Returns: "1234567890"
 */
export function formatPhoneE164(phone: string): string {
  return formatPhone(phone, { format: "e164", includeExtension: false });
}

/**
 * Extracts the last N digits from a phone number.
 *
 * Useful for displaying a short identifier when the caller's identity is unknown.
 *
 * @param phone - The phone number string
 * @param count - Number of digits to extract (default: 2)
 * @param fallback - Fallback value if insufficient digits (default: "##")
 * @returns The last N digits, or the fallback
 *
 * @example
 * lastDigitsOfPhone("+1 (555) 123-4567")
 * // Returns: "67"
 *
 * @example
 * lastDigitsOfPhone("+1 (555) 123-4567", 4)
 * // Returns: "4567"
 *
 * @example
 * lastDigitsOfPhone("abc", 2, "??")
 * // Returns: "??"
 */
export function lastDigitsOfPhone(phone: string, count: number = 2, fallback: string = "##"): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length < count) return fallback;
  return digits.slice(-count);
}

/**
 * Converts a phone number string to its numeric representation by stripping the leading `+`.
 * This is the reverse of `phoneNumberToString`.
 *
 * @example
 * phoneStringToNumber("+13038564271") // Returns: 13038564271
 * phoneStringToNumber("13038564271")  // Returns: 13038564271
 */
export const phoneStringToNumber = (value: string): number => Number(value.replace(/^\+/, ""));

/**
 * Converts a numeric phone number (e.g. from URL param coercion) back to an E.164 string
 * by restoring the leading `+`. String values are returned as-is.
 *
 * @example
 * phoneNumberToString(13038564271)    // Returns: "+13038564271"
 * phoneNumberToString("+13038564271") // Returns: "+13038564271"
 */
export const phoneNumberToString = (value: string | number): string => {
  if (typeof value === "number") return `+${value}`;
  return value;
};
